import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ChevronBottom, Search } from "@zkt/icons";
import {
  I18nContext,
  router,
  setHotelInfo,
  useAuth,
  useLocalStorage,
  useMerchant,
} from "@zkt/eb-utility";
import { Table, Popover, Badge, message, Typography } from "@zkt/components";

import { getUserSetDefaultHoelByUserId } from "@zkt/open-api/esm/user-api";
import {
  getEbUserGetMerchantsByCurrentUser,
  postEbUserChangeUserCurrentHotelPost,
} from "@zkt/open-api/esm/oauth2";
import { iframeClick$ } from "@/observables/iframe-message";
import { I18nTextType } from "./i18n";
import "./hotel-info.scss";

const { Paragraph } = Typography;

interface Hotel {
  id: number;
  name: string;
  brandId: number;
  city: string;
}

export default function UserPopover(): JSX.Element {
  const i18n = useContext<I18nTextType>(I18nContext);
  const storage = useLocalStorage();
  const [visible, setVisible] = useState(false);
  const [word, setWord] = useState("");
  const { merchant } = useMerchant();
  const { auth } = useAuth();
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [pageHotels, setPageHotels] = useState<Hotel[]>([]);
  const [pageSize] = useState(10);
  const [current, setCurrent] = useState(1);
  const [total, setTotal] = useState(0);

  // iframe内部点击时关闭当前弹窗
  useEffect(() => {
    const subscription = iframeClick$.subscribe(() => setVisible(false));
    return () => subscription.unsubscribe();
  }, []);

  const loadHotelList = useCallback(() => {
    getEbUserGetMerchantsByCurrentUser().then((items) =>
      setHotels(
        items.map(({ hotelId, brandId, hotelName, cityName }) => ({
          id: hotelId,
          name: hotelName,
          brandId,
          city: cityName,
        }))
      )
    );
  }, []);
  // 获取酒店列表
  useEffect(() => {
    loadHotelList();
  }, [loadHotelList]);

  useEffect(() => {
    setTotal(pageHotels?.length || 0);
  }, [pageHotels]);

  useEffect(() => {
    setCurrent(1);
    setPageHotels(
      hotels?.filter((h) => !word || `${h.name}${h.id}${h.city}`.includes(word))
    );
  }, [hotels, word]);

  const loadPageHotels = useCallback((index: number) => {
    setCurrent(index);
  }, []);

  // 选择酒店
  const handleHotelSelect = useCallback(
    (hotelId: number, brandId: number) => {
      if (hotelId === +auth?.merchantId) return;
      postEbUserChangeUserCurrentHotelPost({
        changeHotelId: hotelId,
        callbackUrl: window.location.origin,
      })
        .then((data) => {
          // console.log("changeUserCurrentHotel data", data);
          window.location.href = data?.changeHotelUrl || "";
          storage.setItem("defaultHotelId", String(hotelId));
          setHotelInfo(hotelId, brandId);
          setVisible(false);
        })
        .catch((err) => {
          message.error(err.message);
        });
    },
    [auth?.merchantId, storage]
  );

  // 设置默认酒店
  const handleDefaultHotel = useCallback(
    (hotelId: number, brandId: number) => {
      getUserSetDefaultHoelByUserId({ hotelId, userId: auth?.userId }).then(
        () => {
          message.success("设置默认酒店成功");
          handleHotelSelect(hotelId, brandId);
          // 更新hotels
          const hotelsCopy = JSON.parse(JSON.stringify(hotels));
          const selectHotel = hotelsCopy.find((hotel) => hotel.id === hotelId);
          const idx = hotelsCopy.findIndex((hotel) => hotel.id === hotelId);
          hotelsCopy.splice(idx, 1);
          hotelsCopy.splice(0, 0, selectHotel);
          setHotels(hotelsCopy);
        }
      );
    },
    [auth?.userId, handleHotelSelect, hotels]
  );

  // 切换到用户选择的默认酒店
  useEffect(() => {
    if (!hotels?.length || !auth) return;
    const { href } = window.location;
    const defaultHotelId = Number(storage.getItem("defaultHotelId"));
    if (href.includes("_source=login")) {
      if (defaultHotelId && auth?.merchantId !== defaultHotelId) {
        const hotel = hotels.find((x) => x.id === defaultHotelId);
        if (hotel) {
          handleHotelSelect(hotel.id, hotel.brandId);
        }
      }
      const url = href.replace(/[?&]?_source=[^&]*/g, "");
      router.replace(url);
    }
  }, [hotels, storage, auth, handleHotelSelect]);

  const columns = useMemo(
    () => [
      {
        title: "商户名称",
        dataIndex: "name",
        render: (name) => (
          <Paragraph
            ellipsis={{
              rows: 2,
              expandable: true,
              symbol: "展开",
              onExpand: (e) => e.stopPropagation(),
            }}
            className="mb-0"
          >
            {name}
          </Paragraph>
        ),
      },
      { title: "ID", dataIndex: "id", width: 80 },
      { title: "所在城市", dataIndex: "city", width: 90 },
      {
        title: "操作",
        dataIndex: "action",
        className: "hotel-action",
        width: 100,
        render: (_, h) => (
          <button
            type="button"
            disabled={h.id === hotels?.[0]?.id}
            onClick={() => handleDefaultHotel(h.id, h.brandId)}
          >
            {h.id === hotels?.[0]?.id ? i18n.default : i18n.setDefault}
          </button>
        ),
      },
    ],
    [handleDefaultHotel, hotels, i18n.default, i18n.setDefault]
  );
  const handleWordInput = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      setWord(e.currentTarget.value);
    },
    []
  );

  const HotelList = (
    <>
      <header>{i18n.selectHotel}</header>
      <div className="search-box">
        <Search />
        <input
          onInput={handleWordInput}
          placeholder={i18n.hotelNamePlaceholer}
        />
      </div>
      <div className="hotel-list">
        <Table<Hotel>
          onRow={(h) => ({
            onClick: () => handleHotelSelect(h.id, h.brandId),
          })}
          size="small"
          rowKey="id"
          columns={columns}
          dataSource={pageHotels}
          rowClassName={(h) =>
            `single-hotel ${
              (h.id === merchant?.merchantId && "disabled") || "active"
            }`
          }
          pagination={{
            hideOnSinglePage: true,
            showTotal: (t) => (
              <span className="pagination-text">共 {t} 条</span>
            ),
            pageSize,
            current,
            total,
            onChange: loadPageHotels,
            showSizeChanger: false,
          }}
        />
      </div>
    </>
  );
  const popoverVisible = useMemo(() => {
    // 若员工只关联一个酒店，并且是当前展示酒店则不显示列表
    const onlyOneHotelOnShow =
      hotels?.length === 1 && merchant?.merchantId === hotels[0].id;
    return hotels?.length && !onlyOneHotelOnShow;
  }, [hotels, merchant?.merchantId]);
  return (
    <Popover
      placement="bottomLeft"
      content={HotelList}
      className="main-index-hotel-info"
      trigger="click"
      visible={popoverVisible && visible}
      onVisibleChange={setVisible}
      overlayClassName="main-index-popover-content"
    >
      <span>{merchant?.merchantName || ""}</span>
      <Badge count={`ID:${merchant?.merchantId || 0}`} />
      {!!popoverVisible && <ChevronBottom />}
    </Popover>
  );
}
